import {
    ExternalApiTarget,
    LdenGuideline,
    MeasuringSchedule,
    SoundClipAlarmSource,
} from '@/graphql/types';
import type { AirConfig, SoundConfig, VibrationConfig } from '@/graphql/types';
import { convertUnits, roundConvertedDistanceValueForDisplay } from 'assets/js/utils/formatting';
import type { Profile } from 'assets/js/profile';
import {
    difference,
    intersection,
    isArray,
    isEmpty,
    isEqual,
    isFunction,
    isMatch,
    isObject,
    matches,
} from 'lodash-es';
import { useObservableState } from 'observable-hooks';
import { useEffect } from 'react';
import type { Observable, OperatorFunction, Subscription } from 'rxjs';
import {
    BehaviorSubject,
    combineLatest,
    connect,
    distinctUntilChanged,
    map,
    pairwise,
    ReplaySubject,
    Subject,
    withLatestFrom,
    startWith,
} from 'rxjs';
import { extractSecondsFromLeqInterval } from '../../../utils/leq-interval-converter';
import { Markdown } from '../../../utils/markdown';
import { applyNamingConvention, namingConventions$ } from '../../../utils/naming-conventions';
import { confirm, ConfirmType } from '../../confirm/Confirm';
import { NOTIFICATION_VARIANT, useNotifications } from '../../notifications/Notifications';
import type { SwarmFormController } from './form-controller';
import { FormValuesHandler } from './handlers';
import { getFieldOptionLabel } from './option-utils';
import type { FormFieldNames, TranslatedFormField, TranslatedFormSchema } from './types';

type FormValues = VibrationConfig &
    SoundConfig &
    AirConfig & {
        ldenGuideline?: LdenGuideline;
        externalApiTarget?: ExternalApiTarget;
        measuringSchedule?: MeasuringSchedule;
        location?: string | null;
    };

interface WarningCriteria {
    [key: string]: unknown;
}

type WarningCriteriaFunction = (values: FormValues) => boolean;
type DirectTriggerFieldsFunction = (values: FormValues) => FormFieldNames[];

type Field = Pick<TranslatedFormField, 'name' | 'options'>;

interface FormSchema {
    fields: {
        getByName: (name: string) => Field;
    };
}

type WarningMessageFunction = (values: FormValues, formSchema: FormSchema) => string;

interface Warning {
    criteria: WarningCriteria | WarningCriteriaFunction;
    responsibleFields?: string[];
    directTriggerFields?: DirectTriggerFieldsFunction;
    fix?: Record<string, unknown> | null;
    autoFix?: boolean;
    message: string | WarningMessageFunction;
    reComplain?: boolean;
}

interface ProcessedWarning extends Warning {
    responsibleFields: string[];
}

type SoundIntervalField = keyof Pick<
    SoundConfig,
    'lxEqInterval' | 'lXtMinMaxInterval' | 'lxPeakInterval'
>;

function getSoundClipIntervalField(
    soundClipAlarmSource: SoundClipAlarmSource | null | undefined
): SoundIntervalField | null {
    switch (soundClipAlarmSource) {
        case SoundClipAlarmSource.Leq:
            return 'lxEqInterval';
        case SoundClipAlarmSource.Lmax:
            return 'lXtMinMaxInterval';
        case SoundClipAlarmSource.Lpeak:
            return 'lxPeakInterval';
        default:
            return null;
    }
}

function getSoundClipAlarmSource(intervalField: SoundIntervalField): SoundClipAlarmSource | null {
    switch (intervalField) {
        case 'lxEqInterval':
            return SoundClipAlarmSource.Leq;
        case 'lXtMinMaxInterval':
            return SoundClipAlarmSource.Lmax;
        case 'lxPeakInterval':
            return SoundClipAlarmSource.Lpeak;
        default:
            return null;
    }
}

function calculateMidpointOfInterval(interval: SoundConfig[SoundIntervalField]): number {
    return (extractSecondsFromLeqInterval(interval) ?? 0) / 2;
}

function createOneSecondIntervalWarning(
    formSchema: TranslatedFormSchema,
    intervalField: SoundIntervalField
): Warning {
    return {
        criteria: { [intervalField]: 'interval1Second' },
        fix: { [intervalField]: 'interval5Seconds' },
        message() {
            const soundClipAlarmSourceField = formSchema.fields.getByName('soundClipAlarmSource');
            const soundClipAlarmSource = getSoundClipAlarmSource(intervalField);
            const soundClipType = soundClipAlarmSource
                ? getFieldOptionLabel(soundClipAlarmSourceField, soundClipAlarmSource) ?? ''
                : '';

            return interpolate(gettext('INTERVAL_1_SECOND_WARNING'), { soundClipType }, true);
        },
    };
}

function createShortLxnIntervalWarning(): Warning {
    return {
        criteria(values) {
            const intervalInSeconds = extractSecondsFromLeqInterval(values.lXnInterval);
            return (
                intervalInSeconds !== null && intervalInSeconds > 0 && intervalInSeconds < 20 * 60
            );
        },
        responsibleFields: ['lXnInterval'],
        fix: { lXnInterval: 'interval20Minutes' },
        message: gettext('LXN_INTERVAL_BELOW_20_MINUTES_WARNING'),
    };
}

function createSoundClipSourceIntervalDisabledWarning(
    formSchema: TranslatedFormSchema,
    soundClipAlarmSource: SoundClipAlarmSource,
    intervalField: SoundIntervalField
): Warning {
    return {
        criteria: {
            soundClipAlarmSource,
            [intervalField]: 'intervalDisabled',
        },
        responsibleFields: ['soundClipAlarmSource', intervalField],
        directTriggerFields: () => ['soundClipAlarmSource', intervalField],
        fix: {
            [intervalField]: 'interval5Seconds',
        },
        message() {
            const soundClipAlarmSourceField = formSchema.fields.getByName('soundClipAlarmSource');
            const soundClipType =
                getFieldOptionLabel(soundClipAlarmSourceField, soundClipAlarmSource) ?? '';

            return interpolate(
                gettext('SOUND_CLIP_SOURCE_INTERVAL_DISABLED_WARNING'),
                { soundClipType },
                true
            );
        },
    };
}

function createTraceSaveLevelWarning(
    formSchema: TranslatedFormSchema,
    profile: Profile
): Warning | null {
    let traceSaveLevelField;
    try {
        traceSaveLevelField = formSchema.fields.getByName('traceSaveLevel');
    } catch {
        // In case of SWARM Air we don't have the field, so return null for this.
        // This causes the whole check to be excluded from the config warnings.
        return null;
    }
    // `convertUnits` will only convert on SWARM Vibration. For SWARM Sound the value doesn't
    // get converted. As the min max validation of SWARM Sound doesn't come near this value,
    // it doesn't really matter that it's still there even though it's not relevant.
    const convertedWiseMinSaveTraceLevel = convertUnits(traceSaveLevelField, 0.7, profile);
    return {
        criteria(values) {
            // TODO: https://github.com/omnidots/website/issues/8954
            // Ideally we would increase this 0.7 based on the lowest point the vkar line
            // multiplied by values.alarmLevel, but that seems more work, so for now we at
            // least keep the 0.7 as that used to be the traceSaveLevel before we lowered it.
            return Boolean(
                // Don't trigger if the value is missing or is zero (they might still be typing).
                values.traceSaveLevel && values.traceSaveLevel < convertedWiseMinSaveTraceLevel
            );
        },
        responsibleFields: ['traceSaveLevel'],
        fix: {
            traceSaveLevel: roundConvertedDistanceValueForDisplay(
                traceSaveLevelField,
                convertedWiseMinSaveTraceLevel,
                profile.distanceUnit
            ),
        },
        autoFix: true,
        message: gettext('LOW_TRACE_SAVE_LEVEL_WARNING'),
        reComplain: true,
    };
}

// Wrapping this in a function to make the translation evaluate on element rendering.
function createWarnings(formSchema: TranslatedFormSchema, profile: Profile): ProcessedWarning[] {
    const isSbrA2017Foundation = matches({
        guideLine: 'SBR_A_2017',
        buildingLevel: 'foundation',
    });

    const isAtopEnabled = matches({ atopEnabled: true });

    const atopWarningFields = ['guideLine', 'buildingLevel', 'atopEnabled'];

    const wiseMinLogFlushInterval = 5;
    const traceSaveLevelWarning = createTraceSaveLevelWarning(formSchema, profile);

    // The intent of these warnings is to inform users of configuration issues that may lead
    // to undesirable behavior or results within the system and, where applicable, to propose
    // fixes or guide the user in making adjustments.
    const warnings: Warning[] = [
        {
            criteria: { vtopEnabled: false },
            fix: { vtopEnabled: true },
            message: gettext('VTOP_DISABLE_WARNING'),
        },
        {
            criteria(values) {
                return isSbrA2017Foundation(values) && !isAtopEnabled(values);
            },
            responsibleFields: atopWarningFields,
            fix: { atopEnabled: true },
            autoFix: true,
            message: gettext('ATOP_DISABLE_WARNING'),
        },
        {
            criteria(values) {
                return !isSbrA2017Foundation(values) && isAtopEnabled(values);
            },
            responsibleFields: atopWarningFields,
            fix: { atopEnabled: false },
            autoFix: true,
            message: gettext('ATOP_ENABLE_WARNING'),
        },
        {
            criteria: { guideLine: 'SN640312a', vectorEnabled: false },
            fix: { vectorEnabled: true },
            autoFix: true,
            message: gettext('VECTOR_DISABLE_WARNING'),
        },
        {
            criteria(values) {
                return (
                    values.logFlushInterval !== null &&
                    values.logFlushInterval !== undefined &&
                    values.logFlushInterval < wiseMinLogFlushInterval
                );
            },
            responsibleFields: ['logFlushInterval'],
            fix: { logFlushInterval: wiseMinLogFlushInterval },
            autoFix: true,
            message: gettext('LOW_LOG_FLUSH_INTERVAL_WARNING'),
            reComplain: true,
        },
        ...(traceSaveLevelWarning ? [traceSaveLevelWarning] : []),
        // Warn when the selected sound clip trigger has an interval that might not be fully
        // captured by the configured pre-trigger and post-trigger times.
        {
            criteria(values) {
                const intervalField = getSoundClipIntervalField(values.soundClipAlarmSource);
                if (!intervalField) {
                    return false;
                }

                // Warning should not trigger when the user hits backspace in the input fields.
                if (values.tracePreTrigger == null || values.tracePostTrigger == null) {
                    return false;
                }

                const midpoint = calculateMidpointOfInterval(values[intervalField]);
                // Check if the midpoint of the Leq/Lmax/Lpeak interval exceeds either
                // the pre-trigger or post-trigger time.
                // This condition helps determine if the currently configured trigger times are
                // inadequate to fully cover the central portion of the selected interval,
                // indicating
                // a potential loss of sound clip coverage at the beginning or
                // end of the interval.
                return midpoint > values.tracePreTrigger || midpoint > values.tracePostTrigger;
            },
            responsibleFields: [
                'soundClipAlarmSource',
                'lxEqInterval',
                'lXtMinMaxInterval',
                'lxPeakInterval',
                'tracePreTrigger',
                'tracePostTrigger',
            ],
            directTriggerFields(values) {
                const intervalField = getSoundClipIntervalField(values.soundClipAlarmSource);
                return intervalField
                    ? ['soundClipAlarmSource', intervalField, 'tracePreTrigger', 'tracePostTrigger']
                    : [];
            },
            fix: null,
            message(values) {
                const intervalField = getSoundClipIntervalField(values.soundClipAlarmSource);
                const field = formSchema.fields.getByName(intervalField ?? 'lxEqInterval');
                const soundClipAlarmSourceField =
                    formSchema.fields.getByName('soundClipAlarmSource');
                const midpoint = calculateMidpointOfInterval(
                    intervalField ? values[intervalField] : null
                );
                const preSecondsLost = Math.max(0, midpoint - values.tracePreTrigger!);
                const postSecondsLost = Math.max(0, midpoint - values.tracePostTrigger!);

                return interpolate(
                    gettext('SOUND_CLIP_DOES_NOT_COVER_INTERVAL_WARNING'),
                    {
                        soundClipType:
                            getFieldOptionLabel(
                                soundClipAlarmSourceField,
                                values.soundClipAlarmSource ?? null
                            ) ?? '',
                        interval:
                            getFieldOptionLabel(
                                field,
                                intervalField ? values[intervalField] ?? null : null
                            ) ?? '',
                        preTrigger: values.tracePreTrigger ?? '',
                        postTrigger: values.tracePostTrigger ?? '',
                        preSecondsLost,
                        postSecondsLost,
                    },
                    true
                );
            },
        },
        // Excessive battery and data usage warning:
        ...(['lxEqInterval', 'lXtMinMaxInterval', 'lxPeakInterval'] as const).map((intervalField) =>
            createOneSecondIntervalWarning(formSchema, intervalField)
        ),
        createShortLxnIntervalWarning(),
        ...(
            [
                [SoundClipAlarmSource.Leq, 'lxEqInterval'],
                [SoundClipAlarmSource.Lmax, 'lXtMinMaxInterval'],
                [SoundClipAlarmSource.Lpeak, 'lxPeakInterval'],
            ] as const
        ).map(([soundClipAlarmSource, intervalField]) =>
            createSoundClipSourceIntervalDisabledWarning(
                formSchema,
                soundClipAlarmSource,
                intervalField
            )
        ),
        // LDEN guideline / measuring schedule incompatibility warning:
        {
            criteria(values) {
                return (
                    values.externalApiTarget !== ExternalApiTarget.NewYorkApi &&
                    values.ldenGuideline === LdenGuideline.LdenGuidelineNone &&
                    values.measuringSchedule === MeasuringSchedule.LdenTimeSettings
                );
            },
            responsibleFields: ['ldenGuideline', 'measuringSchedule'],
            fix: { measuringSchedule: MeasuringSchedule.EntireDay },
            autoFix: true,
            message: gettext('LDEN_MEASURING_SCHEDULE_INCOMPATIBLE_WARNING'),
        },
        // NY DEP requires continuous measurement data, independently of the LDEN guideline.
        {
            criteria(values) {
                return (
                    values.externalApiTarget === ExternalApiTarget.NewYorkApi &&
                    values.measuringSchedule !== MeasuringSchedule.EntireDay
                );
            },
            responsibleFields: ['externalApiTarget', 'measuringSchedule'],
            fix: { measuringSchedule: MeasuringSchedule.EntireDay },
            autoFix: true,
            message: gettext('NY_DEP_MEASURING_SCHEDULE_REQUIRED_WARNING'),
        },
        // NY DEP location required warning:
        {
            criteria(values: FormValues) {
                return (
                    values.externalApiTarget === ExternalApiTarget.NewYorkApi &&
                    !values.location?.trim()
                );
            },
            responsibleFields: ['externalApiTarget', 'location'],
            message: gettext('NY_DEP_LOCATION_REQUIRED_WARNING'),
        },
    ];

    return warnings.map((warning): ProcessedWarning => {
        if (isFunction(warning.criteria) && !isArray(warning.responsibleFields)) {
            throw new Error(
                'When the warning `criteria` is a function, you also needs to specify ' +
                    '`responsibleFields` in order to make the code understand which dirty ' +
                    'form values are responsible for triggering the warning.'
            );
        }

        return {
            ...warning,
            responsibleFields:
                warning.responsibleFields ?? Object.keys(warning.criteria as WarningCriteria),
        };
    });
}

function matchWarningCriteria(
    criteria: WarningCriteria | WarningCriteriaFunction,
    values: FormValues
): boolean {
    if (isFunction(criteria)) {
        return criteria(values);
    }

    if (isObject(criteria)) {
        return isMatch(values, criteria);
    }

    throw new Error(`Don't know how to match criteria of type ${typeof criteria}`);
}

type ChangedFieldsTuple = readonly [FormValues, string[]];

function trackChangedFields(): OperatorFunction<FormValues, ChangedFieldsTuple> {
    return connect(
        (sharedSource$: Observable<FormValues>) =>
            // Emit [current, changedFields] starting from the first real form
            // value, so downstream handlers don't stall on initial render.
            sharedSource$.pipe(
                startWith(null as FormValues | null),
                pairwise(),
                map(([previousValues, currentValues]) => {
                    if (currentValues === null) {
                        throw new Error(
                            'Configuration warning stream emitted a null current value.'
                        );
                    }

                    if (previousValues === null) {
                        return [currentValues, []] as ChangedFieldsTuple;
                    }

                    const changedKeys = Array.from(
                        new Set([...Object.keys(previousValues), ...Object.keys(currentValues)])
                    ).reduce((keys: string[], key) => {
                        if (
                            !isEqual(
                                previousValues[key as keyof FormValues],
                                currentValues[key as keyof FormValues]
                            )
                        ) {
                            keys.push(key);
                        }
                        return keys;
                    }, []);

                    return [currentValues, changedKeys] as ChangedFieldsTuple;
                })
            ),
        {
            connector() {
                return new ReplaySubject<FormValues>(1);
            },
        }
    );
}

function isWarningTriggeredByDirectChange(
    warning: ProcessedWarning,
    values: FormValues,
    changedFields: string[]
): boolean {
    const matchingFields = intersection(
        warning.directTriggerFields?.(values) ??
            (warning.fix ? Object.keys(warning.fix) : warning.responsibleFields),
        changedFields
    );
    return matchingFields.length > 0;
}

function isWarningTriggeredByIndirectChange(
    warning: ProcessedWarning,
    changedFields: string[]
): boolean {
    const noDirectChangeFields = difference(
        warning.responsibleFields,
        Object.keys(warning.fix ?? {})
    );
    const matchingFields = intersection(noDirectChangeFields, changedFields);
    return matchingFields.length > 0;
}

function applyFix(
    formController: SwarmFormController,
    warning: ProcessedWarning,
    highlight: boolean = false,
    markAsOverridden: boolean = false
): void {
    if (!warning.fix) return;

    const { formControl } = formController.rhfProps;

    const hasParentProject = formController.parentValues$.getValue() !== null;
    let hasScrolled = false;
    Object.entries(warning.fix).forEach(([fieldName, value]) => {
        if (markAsOverridden && hasParentProject) {
            formController.setOverriddenField(fieldName, true, true);
        }

        formControl.setValue(fieldName, value);

        const fixInput = document.getElementById(`id_${fieldName}`);

        if (!fixInput) return;

        // Scroll only to the first fixable input, not every one in the loop.
        if (highlight && !hasScrolled) {
            fixInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            requestAnimationFrame(() => {
                fixInput.classList.add('border-yellow-400');
                setTimeout(() => {
                    fixInput.classList.remove('border-yellow-400');
                }, 4000);
            });
            hasScrolled = true;
        }
    });
}

interface TriggeredWarning extends ProcessedWarning {
    message: string;
}

export class ConfigurationWarningsHelper extends FormValuesHandler {
    allWarnings: ProcessedWarning[];

    triggeredWarnings$: Subject<[TriggeredWarning[], TriggeredWarning[]]>;

    dismissedWarnings$: BehaviorSubject<Array<WarningCriteria | WarningCriteriaFunction>>;

    subscription: Subscription;

    constructor(formController: SwarmFormController) {
        super();

        this.allWarnings = createWarnings(formController.formSchema, formController.profile);
        this.triggeredWarnings$ = new Subject<[TriggeredWarning[], TriggeredWarning[]]>();

        // Keep track of warning criteria that were shown in an alert popup but got dismissed.
        // 'dismissed' means that the error will not re-appear during this form edit session.
        this.dismissedWarnings$ = new BehaviorSubject<
            Array<WarningCriteria | WarningCriteriaFunction>
        >([]);

        const values$ = this.values$.pipe(
            // Occasionally, after user input triggers a value change (via onChange), the form
            // emits a second "phantom" values update caused by a change in the form's error
            // state—not by an actual data change. This can cause `trackChangedFields` to miss
            // real field modifications, as the final emission appears unchanged. Applying
            // `distinctUntilChanged` filters out these redundant emissions, ensuring only
            // genuine value changes are processed, so warnings trigger reliably.
            distinctUntilChanged(isEqual),
            trackChangedFields()
        );

        this.subscription = combineLatest([values$, this.dismissedWarnings$, namingConventions$])
            .pipe(
                withLatestFrom(formController.serverValuesUpdate$),
                map(
                    ([
                        [[values, changedFields], dismissedWarnings, namingConventions],
                        serverValuesUpdate,
                    ]) => {
                        // Determine the triggered warnings by matching the warning criteria
                        // against the form values and render the warning messages.
                        const triggeredWarnings: TriggeredWarning[] = this.allWarnings
                            .filter((warning) => matchWarningCriteria(warning.criteria, values))
                            .map(
                                (warning): TriggeredWarning => ({
                                    ...warning,
                                    message: applyNamingConvention(
                                        isFunction(warning.message)
                                            ? warning.message(
                                                  values,
                                                  formController.formSchema as FormSchema
                                              )
                                            : warning.message,
                                        namingConventions
                                    ),
                                })
                            );

                        // Create an extra list of warnings that are triggered by the current
                        // user's form changes.
                        const triggeredWarningsByDirtyResponsibleFields = triggeredWarnings
                            // Ignore warnings triggered by values coming from the server.
                            .filter(() => !serverValuesUpdate)
                            // Ignore warnings that are already dismissed.
                            .filter((warning) => !dismissedWarnings.includes(warning.criteria))
                            // Only use warnings that are triggered by dirty fields.
                            .filter((warning) =>
                                isWarningTriggeredByDirectChange(warning, values, changedFields)
                            );

                        // A warning is considered auto-fixable if it is marked with `autoFix: true`
                        // and the triggering change was not on the field that requires fixing.
                        // For example, consider the atopWarning. The responsible fields for the
                        // warning condition are `['guideLine', 'buildingLevel', 'atopEnabled']`.
                        // The field that needs fixing is `atopEnabled`. If a change was made to
                        // `['guideLine', 'buildingLevel']`, we can safely auto-fix the issue. If
                        // the user directly changes `atopEnabled`, we don't want to auto-fix as
                        // it would essentially undo the user's UI action. In that case, we just
                        // want to show the warning popup.
                        const autoFixableWarnings = triggeredWarnings.filter((warning) => {
                            // Ignore warnings triggered by values coming from the server.
                            if (serverValuesUpdate) {
                                return false;
                            }

                            if (!warning.autoFix) {
                                return false;
                            }

                            return (
                                !isWarningTriggeredByDirectChange(warning, values, changedFields) &&
                                isWarningTriggeredByIndirectChange(warning, changedFields)
                            );
                        });

                        if (!isEmpty(autoFixableWarnings)) {
                            // If auto-fixes are found, apply them. This will trigger a new
                            // `values$` emission, restarting the entire chain.
                            autoFixableWarnings.forEach((warning) => {
                                applyFix(formController, warning);
                            });

                            // Do not emit stable, as the form state is about to change.
                            return;
                        }

                        // Update the warnings to be displayed in the UI.
                        this.triggeredWarnings$.next([
                            triggeredWarnings.filter(
                                (warning) => !autoFixableWarnings.includes(warning)
                            ),
                            triggeredWarningsByDirtyResponsibleFields.filter(
                                (warning) => !autoFixableWarnings.includes(warning)
                            ),
                        ]);

                        // If no auto-fixes were applied, the state is stable from this
                        // handler's perspective.
                        this.emitStable(values);
                    }
                )
            )
            .subscribe();
    }

    dismissWarning(warning: ProcessedWarning): void {
        if (warning.reComplain) {
            // `reComplain` warning cannot be dismissed 'forever'.
            return;
        }

        const current = this.dismissedWarnings$.getValue();
        this.dismissedWarnings$.next([...current, warning.criteria]);
    }
}

interface ConfigurationWarningProps {
    formController: SwarmFormController;
}

export function ConfigurationWarning({ formController }: ConfigurationWarningProps) {
    const { addNotification } = useNotifications();

    const [triggeredWarnings = [], triggeredWarningsByDirtyResponsibleFields = []] =
        useObservableState<[TriggeredWarning[], TriggeredWarning[]]>(
            formController.configWarnings.triggeredWarnings$,
            [[], []]
        );

    // Effect for displaying configuration warnings at the top of the form. These warnings
    // are triggered by the currently loaded form values, not just by changes made by the user.
    useEffect(() => {
        const notifications = triggeredWarnings.map((warning) =>
            addNotification({
                message: (
                    <>
                        <Markdown>{warning.message}</Markdown>
                        {warning.fix && (
                            <button
                                className="pl-1 underline"
                                onClick={() => applyFix(formController, warning, true, true)}
                            >
                                {gettext('FIX_WARNING')}
                            </button>
                        )}
                    </>
                ),
                variant: NOTIFICATION_VARIANT.WARNING,
            })
        );

        return () => {
            notifications.forEach((notification) => {
                notification.remove();
            });
        };
    }, [triggeredWarnings, formController, addNotification]);

    // Effect for displaying configuration warning alerts when the user selects a warning
    // configuration.
    useEffect(() => {
        const confirmCloseFunctions = triggeredWarningsByDirtyResponsibleFields.map((warning) =>
            confirm({
                markdownMessage: warning.message.replace(/\n{2,}/g, '\n\n&nbsp;\n\n'),
                forceRead: true,
                ...(warning.fix
                    ? // If the warning is auto fixable, show the confirm modal, which
                      // offers a confirm and cancel button.
                      {
                          onCancel() {
                              return applyFix(formController, warning, false, true);
                          },
                          onConfirm() {
                              return formController.configWarnings.dismissWarning(warning);
                          },
                      }
                    : // If the warning is not fixable, show the info modal, which only
                      // shows an ok button.
                      {
                          afterClose() {
                              formController.configWarnings.dismissWarning(warning);
                          },
                          type: ConfirmType.INFO,
                      }),
            })
        );

        return () => {
            confirmCloseFunctions.forEach((closeFunction) => closeFunction());
        };
    }, [triggeredWarningsByDirtyResponsibleFields, formController]);

    return null;
}
